import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ComposeLineRow, previewComposeLine, previewComposeResult} from './compose-field.model';

interface ComposeFieldColumn {
  name: string;
  sample_values: string[];
}

/**
 * The structured line repeater: an ordered, reorderable list of
 * "source column ← fixed label" lines that compose onto ONE target field,
 * each line rendered only when its column's cell is non-empty for the row
 * (a description assembled from a dozen or more labelled source columns,
 * most of them blank on any one row). Presentational,
 * like `<app-group-card>`: the page owns `rows` (so control subscriptions
 * and payload composition live there); this component renders the list,
 * mutates a line's own controls in place, and emits add/remove/reorder.
 */
@Component({
  selector: 'app-compose-field',
  templateUrl: './compose-field.component.html',
  styleUrls: ['./compose-field.component.scss'],
  standalone: false,
})
export class ComposeFieldComponent {
  @Input() rows: ComposeLineRow[] = [];
  @Input() columns: ComposeFieldColumn[] = [];

  @Output() readonly addLine = new EventEmitter<void>();
  @Output() readonly removeLine = new EventEmitter<ComposeLineRow>();
  @Output() readonly reorder = new EventEmitter<ComposeLineRow[]>();

  // What the operator has typed into a line's column box since its option
  // list opened, by line id. Kept apart from `columnControl`, which only
  // ever holds a real column name - half-typed text must not reach the
  // mapping. No entry means nothing typed: the box shows the picked column.
  private readonly searchText = new Map<number, string>();
  // The line whose column box has focus or an open option list. Only that
  // line's autocomplete renders its options: Material creates every
  // projected option even while its panel is closed.
  optionsLineId: number | null = null;

  /** The text a line's column box shows: what is being typed, else the
   * picked column. */
  columnText(row: ComposeLineRow): string {
    return this.searchText.get(row.id) ?? row.columnControl.value ?? '';
  }

  /** The columns a line's option list offers: those whose name contains
   * the typed text, case-insensitively - all of them while nothing is typed. */
  matchingColumns(row: ComposeLineRow): ComposeFieldColumn[] {
    const typed = (this.searchText.get(row.id) ?? '').trim().toLowerCase();
    return typed ? this.columns.filter(c => c.name.toLowerCase().includes(typed)) : this.columns;
  }

  onColumnFocus(row: ComposeLineRow): void {
    this.optionsLineId = row.id;
  }

  onColumnTyped(row: ComposeLineRow, text: string): void {
    this.searchText.set(row.id, text);
  }

  onColumnPicked(row: ComposeLineRow, column: string): void {
    this.searchText.delete(row.id);
    row.columnControl.setValue(column);
  }

  /** The option list closed. That alone does not end the typing - it also
   * closes when the typed text matches no column, or on Escape - so the
   * text is settled only if the box has lost focus as well. */
  onColumnListClosed(row: ComposeLineRow, input: HTMLInputElement): void {
    if (document.activeElement !== input) {
      this.settleColumnText(row);
      this.releaseOptions(row);
    }
  }

  /** The box lost focus. While the option list is open that is a click on
   * an option, or outside it - either way `onColumnPicked` or
   * `onColumnListClosed` follows, and settling here would re-filter the
   * list under the pointer. */
  onColumnBlur(row: ComposeLineRow, listOpen: boolean): void {
    if (!listOpen) {
      this.settleColumnText(row);
      this.releaseOptions(row);
    }
  }

  private releaseOptions(row: ComposeLineRow): void {
    if (this.optionsLineId === row.id) {
      this.optionsLineId = null;
    }
  }

  /** Text typed but not picked from: a column's exact name (case aside)
   * picks that column, an emptied box clears the line's column, and
   * anything else is dropped, so the box shows the column it had again. */
  private settleColumnText(row: ComposeLineRow): void {
    const typed = this.searchText.get(row.id);
    this.searchText.delete(row.id);
    if (typed === undefined) {
      return;
    }
    const name = typed.trim().toLowerCase();
    if (!name) {
      row.columnControl.setValue(null);
      return;
    }
    const exact = this.columns.find(c => c.name.toLowerCase() === name);
    if (exact) {
      row.columnControl.setValue(exact.name);
    }
  }

  /** The operator switched "continue the line above" on or off. Switched
   * on with no text before, the line gets a single space, so its value
   * does not run into the value of the line above. Switched on, the line
   * also gives up its blank line above: that setting is not offered on a
   * continued line, and must not come back set when the line starts a line
   * of its own again. */
  onContinueChanged(row: ComposeLineRow, checked: boolean): void {
    if (!checked) {
      return;
    }
    if (!row.prefixControl.value) {
      row.prefixControl.setValue(' ');
    }
    if (row.blankAboveControl.value) {
      row.blankAboveControl.setValue(false);
    }
  }

  drop(event: CdkDragDrop<ComposeLineRow[]>): void {
    const reordered = [...this.rows];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.reorder.emit(reordered);
  }

  previewFor(row: ComposeLineRow): string | null {
    return previewComposeLine(row, column => this.firstSample(column));
  }

  previewResult(): string | null {
    return previewComposeResult(this.rows, column => this.firstSample(column));
  }

  private firstSample(column: string): string | undefined {
    return this.columns.find(c => c.name === column)?.sample_values[0];
  }
}
